/**
 * The Warehouse reports as ONE list for every desk (user, 2026-10-16): RSW and CSW reports differ
 * only in the stage of material they read, so each report name appears once and names the desk
 * reports behind it. A login sees a report when it holds that report's code for any stage it covers;
 * the page then switches between the stages it may open.
 *
 * Coverage and columns stay as the legacy desks had them (user, 2026-10-16): a stage is listed only
 * where its desk had the report, and each stage keeps its own definition (WAREHOUSE_REPORTS /
 * CSW_REPORTS). PSW is one more stage here once its desk has reports.
 */
import {
  CSW_REPORT_PERMISSION,
  WAREHOUSE_REPORT_PERMISSION,
  type Permission,
} from "./permissions";
import type { CswReportKey, WarehouseReportKey } from "./warehouse-reports";

/** The desks a warehouse report can read, in the Roles grid's column order. */
export const WAREHOUSE_REPORT_STAGES = ["raw", "condition"] as const;
export type WarehouseReportStage = (typeof WAREHOUSE_REPORT_STAGES)[number];

export const WAREHOUSE_REPORT_STAGE_LABEL: Record<
  WarehouseReportStage,
  string
> = { raw: "RSW", condition: "CSW" };

export interface WarehouseStageReport {
  /** `/reports/warehouse/$slug`. */
  slug: string;
  /** Stage-neutral menu name. */
  menu: string;
  hint: string;
  /** The desk report behind each stage it covers. */
  stages: { raw?: WarehouseReportKey; condition?: CswReportKey };
}

/** The legacy RSW Reports tab order (it holds every CSW report too), then the RSW-only extras. */
export const WAREHOUSE_STAGE_REPORTS: readonly WarehouseStageReport[] = [
  {
    slug: "quality",
    menu: "Quality Based Seed Report",
    hint: "Stock by crop and variety, split by QC and GOT status",
    stages: { raw: "quality", condition: "csw-quality" },
  },
  {
    slug: "crop-variety",
    menu: "Crop Variety Wise Seed Report",
    hint: "Every lot in stock with its SLOC, QC and production details",
    stages: { raw: "crop-variety", condition: "csw-crop-variety" },
  },
  {
    slug: "substandard",
    menu: "Substandard Seed Report",
    hint: "Lots in stock that failed QC or GOT",
    stages: { raw: "substandard", condition: "csw-substandard" },
  },
  {
    slug: "coded",
    menu: "Coded Raw Seed Report",
    hint: "Seed with no decoded variety and how long it has waited",
    stages: { raw: "coded" },
  },
  {
    slug: "reserve",
    menu: "Reserve Seed Report",
    hint: "Lots held for an export order or buyer",
    stages: { raw: "reserve", condition: "csw-reserve" },
  },
  {
    slug: "warehouse-wise",
    menu: "Warehouse wise Report",
    hint: "Every WH, or one WH bin by bin with its empty bins",
    stages: { raw: "warehouse-wise", condition: "csw-warehouse-wise" },
  },
  {
    slug: "daily-qc",
    menu: "Daily QC Result Report",
    hint: "QC results recorded on one day",
    stages: { raw: "daily-qc", condition: "csw-daily-qc" },
  },
  {
    slug: "daily-got",
    menu: "Daily GOT Result Report",
    hint: "Seed in stock whose GOT result came on one day",
    stages: { raw: "daily-got", condition: "csw-daily-got" },
  },
  {
    slug: "qc-ageing",
    menu: "QC Ageing Status Report",
    hint: "QC-OK lots by days since their last test",
    stages: { raw: "qc-ageing", condition: "csw-qc-ageing" },
  },
  {
    slug: "periodical-got",
    menu: "Periodical GOT Report Crop Variety Date Wise",
    hint: "Arrivals in a period with production, QC and GOT details",
    stages: { raw: "periodical-got" },
  },
];

export const warehouseStageReport = (
  slug: string
): WarehouseStageReport | undefined =>
  WAREHOUSE_STAGE_REPORTS.find((r) => r.slug === slug);

/** The code that opens this report at this stage, or null where the stage is not covered. */
export function stageReportCode(
  r: WarehouseStageReport,
  stage: WarehouseReportStage
): Permission | null {
  if (stage === "raw")
    return r.stages.raw ? WAREHOUSE_REPORT_PERMISSION[r.stages.raw] : null;
  return r.stages.condition ? CSW_REPORT_PERMISSION[r.stages.condition] : null;
}

/** Every code behind a report, one per stage it covers. */
export const stageReportCodes = (r: WarehouseStageReport): Permission[] =>
  WAREHOUSE_REPORT_STAGES.flatMap((s) => stageReportCode(r, s) ?? []);

/** The stages of a report this login may open, in column order. */
export const openStages = (
  r: WarehouseStageReport,
  can: (p: Permission) => boolean
): WarehouseReportStage[] =>
  WAREHOUSE_REPORT_STAGES.filter((s) => {
    const code = stageReportCode(r, s);
    return code !== null && can(code);
  });
